import api from 'services/api';
import { OrganizationProps } from './organization-service';
import { ResourceProps } from './resource-service';

export type BuildingProps = {
  uuid?: string;
  name: string;
  address: string;
  accountUuid?: string;
  bitformat?: string;
  organizations?: OrganizationProps[];
  minFac: number | any;
  maxFac: number | any;
  minBadge: number | any;
  maxBadge: number | any;
  resources?: ResourceProps[];
  timezone?: string | null;
};

/**
 * The function `findAllBuildings` makes an asynchronous API call to retrieve a list of buildings and returns the data.
 * The function `findAllBuildings` is returning a promise that resolves to an array of `BuildingProps` objects.
 */
const findAllBuildings = async () => {
  const response = await api.get<BuildingProps[]>('/apiv1/buildings/');
  return response?.data;
};

/**
 * The function `findBuildingById` is an asynchronous function that retrieves a building object from an API based on its UUID.
 * uuid - The `uuid` parameter is a string that represents the unique identifier of a building.
 * The function `findBuildingById` returns a Promise that resolves to the data of a building with the specified UUID.
 */
const findBuildingById = async (uuid: string | undefined) => {
  const response = await api.get<BuildingProps>(`/apiv1/building/${uuid}/`);
  return response?.data;
};

/**
 * The function creates a new building by making a POST request to the '/apiv1/building/' endpoint with the provided newBuilding data.
 * newBuilding - The `newBuilding` parameter is an object of type `BuildingProps`. It represents the data for a new building that you want to
 * create.
 * The function `createBuilding` is returning the `data` property of the `response` object.
 */
const createBuilding = async (newBuilding: BuildingProps) => {
  const response = await api.post<BuildingProps>('/apiv1/building/', newBuilding);
  return response?.data;
};

/**
 * The function `updateBuilding` is an asynchronous function that updates a building using an API call.
 * uuid - A string representing the unique identifier of the building to be updated.
 * updatedBuilding - The `updatedBuilding` parameter is an object of type `BuildingProps` that represents the updated information for a building.
 * This function returns the data from the response of the API call.
 */
const updateBuilding = async (uuid: string | undefined, updatedBuilding: BuildingProps) => {
  const response = await api.put<BuildingProps>(`/apiv1/building/${uuid}/`, updatedBuilding);
  return response?.data;
};

/**
 * The function `deleteBuildingById` is an asynchronous function that deletes a building by its UUID using an API call.
 * uuid - The `uuid` parameter is a string that represents the unique identifier of a building.
 * This function returns the data from the response of the API call.
 */
const deleteBuildingById = async (uuid: string | undefined) => {
  const response = await api.delete<BuildingProps>(`/apiv1/building/${uuid}/`);
  return response?.data;
};

const BuildingService = {
  findAllBuildings,
  findBuildingById,
  createBuilding,
  updateBuilding,
  deleteBuildingById
};

export default BuildingService;
